import React from 'react';
import { format, parseISO } from 'date-fns';
import StatusBadge from './StatusBadge';

const CompetitionCard = ({ 
  competition, 
  onEditClick, 
  onDeleteClick 
}) => {
  const getParticipantName = (participant) => {
    // If we have details, use them
    if (participant.details) {
      switch (participant.participantType) {
        case 'player':
          return participant.details.name || 'Player';
        case 'club':
          return participant.details.name || 'Club';
        case 'coach':
          return participant.details.firstName && participant.details.lastName ? 
            `${participant.details.firstName} ${participant.details.lastName}` : 
            participant.details.name || 'Coach';
        case 'model':
          return participant.details.name || 'Model';
        case 'artist':
          return participant.details.name || 'Artist';
        case 'song':
          // For songs, show title with artist name
          if (participant.details.title) {
            const artistName = participant.details.Artist?.name || 
                              participant.details.artist?.name || 
                              'Unknown Artist';
            return `${participant.details.title} - ${artistName}`;
          }
          return participant.details.name || 'Song';
        default:
          return 'Participant';
      }
    }
    
    // Fallback to direct properties
    return participant.name || 'Participant';
  };

  const getParticipantImage = (participant) => {
    if (!participant.details) return null;
    
    switch (participant.participantType) {
      case 'player':
        return participant.details.profileImageUrl;
      case 'club':
        return participant.details.logo;
      case 'coach':
        return participant.details.profilePicture;
      case 'model':
        return participant.details.profileImageUrl;
      case 'artist':
        // Artists might not have an image field yet
        // You can add an image field later if needed
        return null;
      case 'song':
        // Songs might have a cover image
        return participant.details.cover_image || 
               participant.details.coverImage || 
               participant.details.album?.cover_image_url;
      default:
        return null;
    }
  };

  const getParticipantAltText = (participant) => {
    if (!participant.details) return 'Participant';
    
    switch (participant.participantType) {
      case 'player':
        return participant.details.name || 'Player';
      case 'club':
        return participant.details.name || 'Club';
      case 'coach':
        return participant.details.firstName && participant.details.lastName ? 
          `${participant.details.firstName} ${participant.details.lastName}` : 
          participant.details.name || 'Coach';
      case 'model':
        return participant.details.name || 'Model';
      case 'artist':
        return participant.details.name || 'Artist';
      case 'song':
        return participant.details.title || 'Song';
      default:
        return 'Participant';
    }
  };

  const getParticipantSubInfo = (participant) => {
    if (!participant.details) return null;
    
    switch (participant.participantType) {
      case 'artist':
        // Show country or bio snippet for artists
        if (participant.details.country) {
          return participant.details.country;
        }
        if (participant.details.bio) {
          return participant.details.bio.substring(0, 30) + '...';
        }
        return null;
      
      case 'song':
        // Show genre and duration for songs
        const parts = [];
        if (participant.details.genre) {
          parts.push(participant.details.genre);
        }
        if (participant.details.duration_seconds) {
          const minutes = Math.floor(participant.details.duration_seconds / 60);
          const seconds = participant.details.duration_seconds % 60;
          parts.push(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        }
        return parts.length > 0 ? parts.join(' • ') : null;
      
      case 'player':
        return participant.details.position || null;
      
      case 'club':
        return participant.details.league || participant.details.country || null;
      
      case 'coach':
        return participant.details.experience || participant.details.team || null;
      
      case 'model':
        return participant.details.height ? `${participant.details.height}cm` : null;
      
      default:
        return null;
    }
  };

  // Format category name for display
  const formatCategoryName = (category) => {
    const categoryMap = {
      'player': 'Player',
      'club': 'Club',
      'coach': 'Coach',
      'model': 'Lifestyle Model',
      'artist': 'Artist',
      'song': 'Song'
    };
    return categoryMap[category] || category;
  };

  return (
    <div className="card p-5">
      <div className="flex justify-between items-start mb-4 gap-4">
        <div>
          <h3 className="text-lg font-semibold text-white">{competition.title}</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Category: {formatCategoryName(competition.category)}
          </p>
          <p className="text-sm text-gray-400">
            {format(parseISO(competition.startDate), "MMM dd, yyyy")} -{" "}
            {format(parseISO(competition.endDate), "MMM dd, yyyy")}
          </p>
          <div className="mt-2">
            <StatusBadge status={competition.status} />
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={() => onEditClick(competition)}
            className="btn-secondary !py-1.5 !px-3"
          >
            Edit
          </button>
          <button
            onClick={() => onDeleteClick(competition)}
            className="btn-danger !py-1.5 !px-3"
          >
            Delete
          </button>
        </div>
      </div>
      
      {competition.description && (
        <p className="text-gray-300 mb-4 text-sm">{competition.description}</p>
      )}
      
      <div className="mt-4">
        <h4 className="eyebrow mb-2">
          Participants ({competition.participants?.length || 0})
        </h4>
        <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
          {competition.participants?.length > 0 ? (
            competition.participants.map((participant) => {
              const imageUrl = getParticipantImage(participant);
              const name = getParticipantName(participant);
              const altText = getParticipantAltText(participant);
              const subInfo = getParticipantSubInfo(participant);

              return (
                <div 
                  key={participant.id} 
                  className="flex justify-between items-center bg-surface-900 border border-line rounded-lg px-3 py-2 hover:border-line-strong transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={altText}
                        className="w-9 h-9 rounded-full object-cover flex-shrink-0 border border-line-strong"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-surface-700 border border-line-strong flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-accent-soft">
                          {name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium text-sm truncate text-white" title={name}>
                        {name}
                      </span>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-gray-500 capitalize">
                          {participant.participantType}
                        </span>
                        {subInfo && (
                          <>
                            <span className="text-gray-600">•</span>
                            <span className="text-gray-500 truncate" title={subInfo}>
                              {subInfo}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 ml-4 flex-shrink-0 text-xs text-gray-400 tabular-nums">
                    {participant.votes !== undefined && (
                      <span>Votes: <span className="text-gray-200">{participant.votes}</span></span>
                    )}
                    {participant.rank !== undefined && (
                      <span>Rank: <span className="text-gray-200">{participant.rank}</span></span>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-gray-500 text-sm p-4 text-center border border-line rounded-lg">
              No participants added yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompetitionCard;